import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import RadioGroup from "./RadioGroup";
import { SearchIcon } from "lucide-react";

interface Option {
  value: string;
  label: string;
  count?: string;
}

interface FilterWithRadioProps {
  label?: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  id?: string;
  placeholder?: string;
  maxHeight?: string;
  disabled?: boolean;
  showAbove?: boolean;
  className?: string;
  labelClassName?: string;
  popupWidth?: string;
  /** Form field styling per Figma — rounded 15px, border #E2E8F0, 16px value text */
  fieldStyle?: boolean;
}

const FilterWithRadio: React.FC<FilterWithRadioProps> = ({
  label,
  options,
  value,
  onChange,
  error,
  required = false,
  id,
  placeholder = "Select an option",
  maxHeight = "450px",
  disabled = false,
  showAbove = true,
  className = "",
  labelClassName = "",
  popupWidth,
  fieldStyle = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([
    ...options,
  ]);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const searchInputRef = useRef<HTMLInputElement>(null);
  const rafRef = useRef<number | null>(null);
  const selectedOption = options.find((option) => option.value === value);
  const showSearch = options.length >= 5;

  // Function to update dropdown position
  const updateDropdownPosition = () => {
    if (isOpen && selectRef.current) {
      const selectRect = selectRef.current.getBoundingClientRect();
      const dropdownWidth = popupWidth ? popupWidth : selectRect.width;
      const parsedMaxHeight = parseInt(maxHeight, 10);
      const preferredMaxHeight = Number.isFinite(parsedMaxHeight)
        ? parsedMaxHeight
        : 450;
      const viewportPadding = 8;
      const minUsableHeight = 180;
      const availableBelow = Math.max(
        0,
        window.innerHeight - Math.round(selectRect.bottom) - viewportPadding
      );
      const availableAbove = Math.max(
        0,
        Math.round(selectRect.top) - viewportPadding
      );

      // Viewport-aware placement: keep popup fully visible whenever possible.
      const placeAbove = showAbove
        ? availableAbove > availableBelow && availableAbove >= minUsableHeight
        : availableBelow < minUsableHeight && availableAbove > availableBelow;

      const availableSpace = placeAbove ? availableAbove : availableBelow;
      const computedMaxHeight = Math.max(
        120,
        Math.min(preferredMaxHeight, availableSpace || preferredMaxHeight)
      );
      // Use the real rendered dropdown height for above-placement anchoring.
      // This prevents large visual gaps when content is shorter than maxHeight.
      const renderedContentHeight = dropdownRef.current?.scrollHeight ?? 0;
      const effectiveDropdownHeight =
        renderedContentHeight > 0
          ? Math.min(renderedContentHeight, computedMaxHeight)
          : computedMaxHeight;

      let style: React.CSSProperties = {
        position: "fixed",
        left: Math.round(selectRect.left),
        width: dropdownWidth,
        minWidth: dropdownWidth,
        zIndex: 9999,
        maxHeight: `${computedMaxHeight}px`,
        overflowY: "auto",
      };

      if (placeAbove) {
        style.top = Math.max(
          viewportPadding,
          Math.round(selectRect.top) - effectiveDropdownHeight
        );
      } else {
        style.top = Math.round(selectRect.bottom);
      }
      setDropdownStyle(style);
    }
  };

  // Position dropdown when it opens and on window resize/scroll
  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();

      const scheduleUpdate = () => {
        if (rafRef.current !== null) return;
        rafRef.current = window.requestAnimationFrame(() => {
          updateDropdownPosition();
          rafRef.current = null;
        });
      };

      // Keep anchored on viewport changes without reacting to dropdown inner scroll.
      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate);

      return () => {
        window.removeEventListener("scroll", scheduleUpdate);
        window.removeEventListener("resize", scheduleUpdate);
        if (rafRef.current !== null) {
          window.cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [isOpen, showAbove, maxHeight, popupWidth]);

  // Filter options based on search term while preserving original order
  useEffect(() => {
    // Always maintain the original order from the options array
    if (searchTerm.trim() === "") {
      setFilteredOptions([...options]);
    } else {
      // Filter while preserving the original order
      // Convert search term to lowercase for case-insensitive comparison
      const searchLower = searchTerm.toLowerCase();

      const filtered = options.filter((option) => {
        // Skip the placeholder option (empty value with placeholder text)
        if (option.value === "" && option.label === placeholder) {
          return false;
        }
        // Check if the option label includes the search term (case insensitive)
        return option.label.toLowerCase().includes(searchLower);
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, placeholder]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    // Reset search and filtered options when dropdown closes
    if (!isOpen) {
      setSearchTerm("");
      setFilteredOptions([...options]);
    }
  }, [isOpen, showSearch, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown when parent/page scrolls (outside-click-like behavior)
  useEffect(() => {
    if (!isOpen) return;
    const openedAt = Date.now();

    const handleScrollClose = (event: Event) => {
      if (Date.now() - openedAt < 180) {
        return;
      }
      const targetNode = event.target as Node | null;
      if (
        targetNode &&
        ((dropdownRef.current && dropdownRef.current.contains(targetNode)) ||
          (selectRef.current && selectRef.current.contains(targetNode)))
      ) {
        return;
      }
      setIsOpen(false);
    };

    window.addEventListener("scroll", handleScrollClose, true);
    return () => {
      window.removeEventListener("scroll", handleScrollClose, true);
    };
  }, [isOpen]);

  return (
    <div
      className={`flex flex-col space-y-2 ${className || "w-full"}`.trim()}
    >
      {label ? (
        <label
          htmlFor={id}
          className={`text-[#2D3748] text-[16px] font-medium ${labelClassName}`.trim()}
        >
          {label}
          {required ? <span className="text-[#ef4444] ml-1">*</span> : null}
        </label>
      ) : null}
      <div className="relative w-full" id={id} ref={selectRef}>
        <div
          className={`h-[50px] w-full px-[20px] py-[15px] border bg-white ${
            error ? "border-[#FF3B30]" : fieldStyle ? "border-[#E2E8F0]" : "border-[#E5E7EB]"
          } ${fieldStyle ? "rounded-[15px]" : "rounded-[12px]"} flex items-center justify-between ${
            disabled ? "" : "cursor-pointer"
          }`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span
            className={`${
              value
                ? fieldStyle
                  ? "text-[16px] font-medium text-[#1F1F1F]"
                  : "text-[#1F1F1F] text-[14px] font-[500]"
                : fieldStyle
                  ? "text-[14px] font-medium text-[#A0AEC0]"
                  : "text-[#A0AEC0] text-[14px] font-[500]"
            } flex-1 min-w-0`}
            title={selectedOption ? selectedOption.label : placeholder}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate block">
                {selectedOption ? selectedOption.label : placeholder}
              </span>

              {selectedOption?.count && (
                <span className="text-white bg-[#5C88C5] px-2 py-1 rounded-full w-[20px] h-[20px] flex items-center justify-center text-[12px] font-[500] shrink-0">
                  {selectedOption.count}
                </span>
              )}
            </div>
          </span>
          {!disabled && (
            <svg
              className={`w-5 h-5 text-[#A0AEC0] transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              ></path>
            </svg>
          )}
        </div>

        {isOpen &&
          typeof window !== "undefined" &&
          ReactDOM.createPortal(
            <div
              ref={dropdownRef}
              className={`bg-white border border-gray-200 rounded-md shadow-lg scrollbar-custom ${
                showAbove ? "" : ""
              }`}
              style={{
                ...dropdownStyle,
              }}
            >
              {showSearch && (
                <div className="p-2 sticky top-0 bg-white z-20">
                  <div className="relative z-20 rounded-[12px] bg-white border border-gray-200">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                        <SearchIcon className="w-6 h-6 text-gray-500" />
                      </div>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="w-full pl-10 pr-3 py-2 rounded-md text-sm focus:outline-none"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}
              <RadioGroup
                label=""
                options={filteredOptions}
                value={value}
                onChange={(newValue) => {
                  if (!disabled) {
                    onChange(newValue);
                    setIsOpen(false);
                  }
                }}
                id={id}
              />
              {filteredOptions.length === 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No options found
                </div>
              )}
            </div>,
            document.body
          )}
      </div>
      {error && (
        <p className="mt-0 text-[12px] font-[500] text-[#FF3B30]">{error}</p>
      )}
    </div>
  );
};

export default FilterWithRadio;
